import React, { useState, useCallback } from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  Text,
  Button,
  Input,
  Field,
  Textarea,
  Badge,
  Tooltip,
  Card,
  CardHeader,
  ProgressBar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '../Common/ScrollableMessageBar';
import {
  Add24Regular,
  Delete24Regular,
  ArrowUpload24Regular,
  ArrowDownload24Regular,
  CheckmarkCircle24Regular,
  Warning24Regular,
  ErrorCircle24Regular,
  MoreHorizontal24Regular,
  Copy24Regular,
} from '@fluentui/react-icons';
import { gfcPolicyService } from '../../services/gfcPolicyService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  dualListContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    ...shorthands.gap('24px'),
    minHeight: '400px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      ...shorthands.gap('16px'),
    },
  },
  listCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content',
  },
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.margin('0', '0', '12px', '0'),
  },
  listTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  domainInputContainer: {
    display: 'flex',
    ...shorthands.gap('8px'),
    ...shorthands.margin('0', '0', '12px', '0'),
    '@media (max-width: 480px)': {
      flexDirection: 'column',
      ...shorthands.gap('8px'),
    },
  },
  domainList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    minHeight: '200px',
    maxHeight: '300px',
    overflowY: 'auto',
    ...shorthands.padding('8px'),
    backgroundColor: '#fafafa',
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    ...shorthands.borderRadius('4px'),
  },
  emptyList: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '150px',
    color: '#605e5c',
    textAlign: 'center',
  },
  domainItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '12px'),
    backgroundColor: '#ffffff',
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    ...shorthands.borderRadius('4px'),
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8f9fa',
      ...shorthands.border('1px', 'solid', '#106ebe'),
    },
  },
  domainText: {
    flex: 1,
    fontFamily: 'Consolas, Monaco, monospace',
    fontSize: '14px',
  },
  domainActions: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('4px'),
  },
  validDomain: {
    borderLeftColor: '#107C10',
    borderLeftWidth: '3px',
  },
  invalidDomain: {
    borderLeftColor: '#d13438',
    borderLeftWidth: '3px',
    backgroundColor: '#fde7e9',
  },
  bulkActions: {
    display: 'flex',
    ...shorthands.gap('8px'),
    ...shorthands.margin('0', '0', '16px', '0'),
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  limitsInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('12px'),
    backgroundColor: '#f8f9fa',
    ...shorthands.border('1px', 'solid', '#e1dfdd'),
    ...shorthands.borderRadius('4px'),
  },
  textareaContainer: {
    ...shorthands.margin('16px', '0'),
  },
});

interface EnhancedDomainManagerProps {
  allowedDomains: string[];
  blockedDomains: string[];
  onAllowedDomainsChange: (domains: string[]) => void;
  onBlockedDomainsChange: (domains: string[]) => void;
  communicationMode: string;
  disabled?: boolean;
  showLimits?: boolean;
}

export const EnhancedDomainManager: React.FC<EnhancedDomainManagerProps> = ({
  allowedDomains,
  blockedDomains,
  onAllowedDomainsChange,
  onBlockedDomainsChange,
  communicationMode,
  disabled = false,
  showLimits = true,
}) => {
  const styles = useStyles();
  const [allowedInput, setAllowedInput] = useState('');
  const [blockedInput, setBlockedInput] = useState('');
  const [bulkInput, setBulkInput] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const validateDomain = useCallback((domain: string): boolean => {
    return gfcPolicyService.validateDomainFormat(domain);
  }, []);

  const addDomain = (domain: string, type: 'allowed' | 'blocked') => {
    if (!domain.trim()) return;

    const normalizedDomain = domain.trim().toLowerCase();
    
    if (!validateDomain(normalizedDomain)) {
      setValidationErrors(prev => ({
        ...prev,
        [normalizedDomain]: 'Invalid domain format'
      }));
      return;
    }

    if (type === 'allowed') {
      if (!allowedDomains.includes(normalizedDomain)) {
        onAllowedDomainsChange([...allowedDomains, normalizedDomain]);
      }
      setAllowedInput('');
    } else {
      if (!blockedDomains.includes(normalizedDomain)) {
        onBlockedDomainsChange([...blockedDomains, normalizedDomain]);
      }
      setBlockedInput('');
    }

    // Clear validation error if it exists
    setValidationErrors(prev => {
      const { [normalizedDomain]: removed, ...rest } = prev;
      return rest;
    });
  };

  const removeDomain = (domain: string, type: 'allowed' | 'blocked') => {
    if (type === 'allowed') {
      onAllowedDomainsChange(allowedDomains.filter(d => d !== domain));
    } else {
      onBlockedDomainsChange(blockedDomains.filter(d => d !== domain));
    }
  };

  const processBulkDomains = (text: string, type: 'allowed' | 'blocked') => {
    const domains = text
      .split(/[\n,;]+/)
      .map(d => d.trim().toLowerCase())
      .filter(d => d.length > 0);

    const validDomains: string[] = [];
    const invalidDomains: string[] = [];

    domains.forEach(domain => {
      if (validateDomain(domain)) {
        validDomains.push(domain);
      } else {
        invalidDomains.push(domain);
      }
    });

    if (invalidDomains.length > 0) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        invalidDomains.forEach(domain => {
          newErrors[domain] = 'Invalid domain format';
        });
        return newErrors;
      });
    }

    if (type === 'allowed') {
      const existingDomains = new Set(allowedDomains);
      const newDomains = validDomains.filter(d => !existingDomains.has(d));
      onAllowedDomainsChange([...allowedDomains, ...newDomains]);
    } else {
      const existingDomains = new Set(blockedDomains);
      const newDomains = validDomains.filter(d => !existingDomains.has(d));
      onBlockedDomainsChange([...blockedDomains, ...newDomains]);
    }

    setBulkInput('');
    setShowBulkInput(false);
  };

  const exportDomains = (domains: string[], filename: string) => {
    const blob = new Blob([domains.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (domains: string[]) => {
    navigator.clipboard.writeText(domains.join('\n'));
  };

  const getTotalDomains = () => allowedDomains.length + blockedDomains.length;
  const getProgressValue = () => Math.min((getTotalDomains() / 100) * 100, 100);
  const isNearLimit = () => getTotalDomains() > 80;
  const isOverLimit = () => getTotalDomains() > 100;

  const shouldShowDomainLists = () => {
    return communicationMode === 'AllowSpecificExternalDomains' || 
           communicationMode === 'BlockSpecificExternalDomains';
  };

  const getDomainListIcon = (domain: string) => {
    if (validationErrors[domain]) {
      return <ErrorCircle24Regular style={{ color: '#d13438' }} />;
    }
    return validateDomain(domain) 
      ? <CheckmarkCircle24Regular style={{ color: '#107C10' }} />
      : <Warning24Regular style={{ color: '#ff8c00' }} />;
  };

  if (!shouldShowDomainLists()) {
    return (
      <MessageBar intent="info" data-testid="gfc-enhanced-domain-manager-info-bar">
        <MessageBarBody data-testid="gfc-enhanced-domain-manager-info-body">
          <Text weight="semibold" data-testid="gfc-enhanced-domain-manager-info-title">Domain Management Not Required</Text>
          <br data-testid="gfc-enhanced-domain-manager-info-break" />
          <Text data-testid="gfc-enhanced-domain-manager-info-text">
            The selected communication mode ({communicationMode}) does not require 
            explicit domain configuration. This policy will handle domains automatically 
            based on your organization's settings.
          </Text>
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.container} data-testid="gfc-enhanced-domain-manager">
      {showLimits && (
        <div className={styles.limitsInfo} data-testid="gfc-enhanced-domain-manager-limits-info">
          <div data-testid="gfc-enhanced-domain-manager-limits-text">
            <Text weight="semibold" data-testid="gfc-enhanced-domain-manager-limits-count-text">Domain Count: {getTotalDomains()} / 100</Text>
            <Text size={300} style={{ display: 'block', color: '#605e5c' }} data-testid="gfc-enhanced-domain-manager-limits-description-text">
              GFC policies support up to 100 domains per policy
            </Text>
          </div>
          <div style={{ width: '200px' }} data-testid="gfc-enhanced-domain-manager-limits-progress-wrap">
            <ProgressBar 
              value={getProgressValue()} 
              color={isOverLimit() ? 'error' : isNearLimit() ? 'warning' : 'brand'}
              data-testid="gfc-enhanced-domain-manager-limits-progress"
            />
          </div>
        </div>
      )}

      {isOverLimit() && (
        <MessageBar intent="error" data-testid="gfc-enhanced-domain-manager-limit-error-bar">
          <MessageBarBody data-testid="gfc-enhanced-domain-manager-limit-error-body">
            <Text weight="semibold" data-testid="gfc-enhanced-domain-manager-limit-error-title">Domain Limit Exceeded</Text>
            <br data-testid="gfc-enhanced-domain-manager-limit-error-break" />
            <Text data-testid="gfc-enhanced-domain-manager-limit-error-text">
              You have {getTotalDomains()} domains, which exceeds the GFC limit of 100 per policy. 
              Please remove some domains or use the optimization feature to split into multiple policies.
            </Text>
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.bulkActions} data-testid="gfc-enhanced-domain-manager-bulk-actions">
        <Button
          appearance="subtle"
          icon={<ArrowUpload24Regular />}
          onClick={() => setShowBulkInput(!showBulkInput)}
          disabled={disabled}
          data-testid="gfc-enhanced-domain-manager-bulk-import-toggle"
        >
          Bulk Import
        </Button>
        <Menu data-testid="gfc-enhanced-domain-manager-export-menu">
          <MenuTrigger disableButtonEnhancement data-testid="gfc-enhanced-domain-manager-export-menu-trigger">
            <Button appearance="subtle" icon={<ArrowDownload24Regular />} disabled={disabled} data-testid="gfc-enhanced-domain-manager-export-button">
              Export
            </Button>
          </MenuTrigger>
          <MenuPopover data-testid="gfc-enhanced-domain-manager-export-menu-popover">
            <MenuList data-testid="gfc-enhanced-domain-manager-export-menu-list">
              <MenuItem 
                onClick={() => exportDomains(allowedDomains, 'allowed-domains.txt')}
                disabled={allowedDomains.length === 0}
                data-testid="gfc-enhanced-domain-manager-export-allowed"
              >
                Export Allowed Domains
              </MenuItem>
              <MenuItem 
                onClick={() => exportDomains(blockedDomains, 'blocked-domains.txt')}
                disabled={blockedDomains.length === 0}
                data-testid="gfc-enhanced-domain-manager-export-blocked"
              >
                Export Blocked Domains
              </MenuItem>
              <MenuDivider />
              <MenuItem 
                onClick={() => exportDomains([...allowedDomains, ...blockedDomains], 'all-domains.txt')}
                disabled={getTotalDomains() === 0}
                data-testid="gfc-enhanced-domain-manager-export-all"
              >
                Export All Domains
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>

      {showBulkInput && (
        <div className={styles.textareaContainer} data-testid="gfc-enhanced-domain-manager-bulk-input-container">
          <Field label="Bulk Domain Import" data-testid="gfc-enhanced-domain-manager-bulk-input-field">
            <Textarea
              placeholder="Enter domains separated by commas or new lines..."
              value={bulkInput}
              onChange={(e, data) => setBulkInput(data.value)}
              rows={6}
              data-testid="gfc-enhanced-domain-manager-bulk-input-textarea"
            />
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }} data-testid="gfc-enhanced-domain-manager-bulk-input-actions">
              <Button
                appearance="primary"
                onClick={() => processBulkDomains(bulkInput, 'allowed')}
                disabled={!bulkInput.trim() || communicationMode !== 'AllowSpecificExternalDomains'}
                data-testid="gfc-enhanced-domain-manager-bulk-add-allowed"
              >
                Add to Allowed
              </Button>
              <Button
                appearance="secondary"
                onClick={() => processBulkDomains(bulkInput, 'blocked')}
                disabled={!bulkInput.trim() || communicationMode !== 'BlockSpecificExternalDomains'}
                data-testid="gfc-enhanced-domain-manager-bulk-add-blocked"
              >
                Add to Blocked
              </Button>
              <Button
                appearance="subtle"
                onClick={() => setShowBulkInput(false)}
                data-testid="gfc-enhanced-domain-manager-bulk-cancel"
              >
                Cancel
              </Button>
            </div>
          </Field>
        </div>
      )}

      <div className={styles.dualListContainer} data-testid="gfc-enhanced-domain-manager-dual-list-container">
        {/* Allowed Domains List */}
        {communicationMode === 'AllowSpecificExternalDomains' && (
          <Card className={styles.listCard} data-testid="gfc-enhanced-domain-manager-allowed-card">
            <CardHeader
              header={
                <div className={styles.listHeader} data-testid="gfc-enhanced-domain-manager-allowed-list-header">
                  <div className={styles.listTitle} data-testid="gfc-enhanced-domain-manager-allowed-list-title">
                    <CheckmarkCircle24Regular style={{ color: '#107C10' }} />
                    <Text weight="semibold" size={500} data-testid="gfc-enhanced-domain-manager-allowed-title-text">Allowed Domains</Text>
                    <Badge appearance="filled" color="success" data-testid="gfc-enhanced-domain-manager-allowed-count-badge">
                      {allowedDomains.length}
                    </Badge>
                  </div>
                  <Menu data-testid="gfc-enhanced-domain-manager-allowed-menu">
                    <MenuTrigger disableButtonEnhancement data-testid="gfc-enhanced-domain-manager-allowed-menu-trigger">
                      <Button
                        appearance="subtle"
                        icon={<MoreHorizontal24Regular />}
                        size="small"
                        data-testid="gfc-enhanced-domain-manager-allowed-menu-button"
                      />
                    </MenuTrigger>
                    <MenuPopover data-testid="gfc-enhanced-domain-manager-allowed-menu-popover">
                      <MenuList data-testid="gfc-enhanced-domain-manager-allowed-menu-list">
                        <MenuItem
                          icon={<Copy24Regular />}
                          onClick={() => copyToClipboard(allowedDomains)}
                          data-testid="gfc-enhanced-domain-manager-allowed-copy-all"
                        >
                          Copy All
                        </MenuItem>
                        <MenuItem
                          icon={<ArrowDownload24Regular />}
                          onClick={() => exportDomains(allowedDomains, 'allowed-domains.txt')}
                          data-testid="gfc-enhanced-domain-manager-allowed-export-list"
                        >
                          Export List
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              }
            />
            
            <div style={{ padding: '0 16px 16px' }} data-testid="gfc-enhanced-domain-manager-allowed-list-content">
              <div className={styles.domainInputContainer} data-testid="gfc-enhanced-domain-manager-allowed-input-row">
                <Input
                  placeholder="Enter domain (e.g., contoso.com)"
                  value={allowedInput}
                  onChange={(e, data) => setAllowedInput(data.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      addDomain(allowedInput, 'allowed');
                    }
                  }}
                  disabled={disabled}
                  data-testid="gfc-enhanced-domain-manager-allowed-input"
                />
                <Button
                  icon={<Add24Regular />}
                  appearance="primary"
                  onClick={() => addDomain(allowedInput, 'allowed')}
                  disabled={disabled || !allowedInput.trim()}
                  data-testid="gfc-enhanced-domain-manager-allowed-add-button"
                />
              </div>

              <div className={styles.domainList} data-testid="gfc-enhanced-domain-manager-allowed-domain-list">
                {allowedDomains.length === 0 ? (
                  <div className={styles.emptyList} data-testid="gfc-enhanced-domain-manager-allowed-empty-state">
                    <CheckmarkCircle24Regular style={{ color: '#605e5c', fontSize: '24px' }} />
                    <Text size={300} data-testid="gfc-enhanced-domain-manager-allowed-empty-title">No allowed domains configured</Text>
                    <Text size={200} data-testid="gfc-enhanced-domain-manager-allowed-empty-description">Add domains that users can communicate with</Text>
                  </div>
                ) : (
                  allowedDomains.map((domain, index) => (
                    <div
                      key={domain}
                      className={mergeClasses(
                        styles.domainItem,
                        validateDomain(domain) ? styles.validDomain : styles.invalidDomain
                      )} data-testid={`gfc-enhanced-domain-manager-allowed-domain-item-${index}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid={`gfc-enhanced-domain-manager-allowed-domain-content-${index}`}>
                        {getDomainListIcon(domain)}
                        <Text className={styles.domainText} data-testid={`gfc-enhanced-domain-manager-allowed-domain-text-${index}`}>{domain}</Text>
                      </div>
                      <div className={styles.domainActions} data-testid={`gfc-enhanced-domain-manager-allowed-domain-actions-${index}`}>
                        <Tooltip content="Remove domain" relationship="label" data-testid={`gfc-enhanced-domain-manager-allowed-domain-remove-tooltip-${index}`}>
                          <Button
                            appearance="subtle"
                            icon={<Delete24Regular />}
                            size="small"
                            onClick={() => removeDomain(domain, 'allowed')}
                            disabled={disabled}
                            data-testid={`gfc-enhanced-domain-manager-allowed-domain-remove-${index}`}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Blocked Domains List */}
        {communicationMode === 'BlockSpecificExternalDomains' && (
          <Card className={styles.listCard} data-testid="gfc-enhanced-domain-manager-blocked-card">
            <CardHeader
              header={
                <div className={styles.listHeader} data-testid="gfc-enhanced-domain-manager-blocked-list-header">
                  <div className={styles.listTitle} data-testid="gfc-enhanced-domain-manager-blocked-list-title">
                    <ErrorCircle24Regular style={{ color: '#d13438' }} />
                    <Text weight="semibold" size={500} data-testid="gfc-enhanced-domain-manager-blocked-title-text">Blocked Domains</Text>
                    <Badge appearance="filled" color="danger" data-testid="gfc-enhanced-domain-manager-blocked-count-badge">
                      {blockedDomains.length}
                    </Badge>
                  </div>
                  <Menu data-testid="gfc-enhanced-domain-manager-blocked-menu">
                    <MenuTrigger disableButtonEnhancement data-testid="gfc-enhanced-domain-manager-blocked-menu-trigger">
                      <Button
                        appearance="subtle"
                        icon={<MoreHorizontal24Regular />}
                        size="small"
                        data-testid="gfc-enhanced-domain-manager-blocked-menu-button"
                      />
                    </MenuTrigger>
                    <MenuPopover data-testid="gfc-enhanced-domain-manager-blocked-menu-popover">
                      <MenuList data-testid="gfc-enhanced-domain-manager-blocked-menu-list">
                        <MenuItem
                          icon={<Copy24Regular />}
                          onClick={() => copyToClipboard(blockedDomains)}
                          data-testid="gfc-enhanced-domain-manager-blocked-copy-all"
                        >
                          Copy All
                        </MenuItem>
                        <MenuItem
                          icon={<ArrowDownload24Regular />}
                          onClick={() => exportDomains(blockedDomains, 'blocked-domains.txt')}
                          data-testid="gfc-enhanced-domain-manager-blocked-export-list"
                        >
                          Export List
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              }
            />
            
            <div style={{ padding: '0 16px 16px' }} data-testid="gfc-enhanced-domain-manager-blocked-list-content">
              <div className={styles.domainInputContainer} data-testid="gfc-enhanced-domain-manager-blocked-input-row">
                <Input
                  placeholder="Enter domain to block (e.g., spam.com)"
                  value={blockedInput}
                  onChange={(e, data) => setBlockedInput(data.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      addDomain(blockedInput, 'blocked');
                    }
                  }}
                  disabled={disabled}
                  data-testid="gfc-enhanced-domain-manager-blocked-input"
                />
                <Button
                  icon={<Add24Regular />}
                  appearance="primary"
                  onClick={() => addDomain(blockedInput, 'blocked')}
                  disabled={disabled || !blockedInput.trim()}
                  data-testid="gfc-enhanced-domain-manager-blocked-add-button"
                />
              </div>

              <div className={styles.domainList} data-testid="gfc-enhanced-domain-manager-blocked-domain-list">
                {blockedDomains.length === 0 ? (
                  <div className={styles.emptyList} data-testid="gfc-enhanced-domain-manager-blocked-empty-state">
                    <ErrorCircle24Regular style={{ color: '#605e5c', fontSize: '24px' }} />
                    <Text size={300} data-testid="gfc-enhanced-domain-manager-blocked-empty-title">No blocked domains configured</Text>
                    <Text size={200} data-testid="gfc-enhanced-domain-manager-blocked-empty-description">Add domains to prevent communication</Text>
                  </div>
                ) : (
                  blockedDomains.map((domain, index) => (
                    <div
                      key={domain}
                      className={mergeClasses(
                        styles.domainItem,
                        validateDomain(domain) ? styles.validDomain : styles.invalidDomain
                      )} data-testid={`gfc-enhanced-domain-manager-blocked-domain-item-${index}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} data-testid={`gfc-enhanced-domain-manager-blocked-domain-content-${index}`}>
                        {getDomainListIcon(domain)}
                        <Text className={styles.domainText} data-testid={`gfc-enhanced-domain-manager-blocked-domain-text-${index}`}>{domain}</Text>
                      </div>
                      <div className={styles.domainActions} data-testid={`gfc-enhanced-domain-manager-blocked-domain-actions-${index}`}>
                        <Tooltip content="Remove domain" relationship="label" data-testid={`gfc-enhanced-domain-manager-blocked-domain-remove-tooltip-${index}`}>
                          <Button
                            appearance="subtle"
                            icon={<Delete24Regular />}
                            size="small"
                            onClick={() => removeDomain(domain, 'blocked')}
                            disabled={disabled}
                            data-testid={`gfc-enhanced-domain-manager-blocked-domain-remove-${index}`}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        )}
      </div>

      {Object.keys(validationErrors).length > 0 && (
        <MessageBar intent="error" data-testid="gfc-enhanced-domain-manager-validation-errors-bar">
          <MessageBarBody data-testid="gfc-enhanced-domain-manager-validation-errors-body">
            <Text weight="semibold" data-testid="gfc-enhanced-domain-manager-validation-errors-title">Domain Validation Errors</Text>
            <br data-testid="gfc-enhanced-domain-manager-validation-break" />
            {Object.entries(validationErrors).map(([domain, error], index) => (
              <Text key={domain} size={200} style={{ display: 'block' }} data-testid={`gfc-enhanced-domain-manager-validation-error-${index}`}>
                • {domain}: {error}
              </Text>
            ))}
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};